import { useEffect, useState } from "react";
import { ImageIcon, Megaphone } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SecureImage } from "@/components/SecureImage";
import { api, formatApiErrorDetail } from "@/lib/api";

export function BannerReview({ banners, onRefresh }) {
  const [items, setItems] = useState(banners);

  useEffect(() => setItems(banners), [banners]);

  const toggle = async (banner) => {
    try {
      const response = await api.patch(`/admin/banners/${banner.id}`, { include_in_carousel: !banner.include_in_carousel });
      setItems((current) => current.map((item) => (item.id === banner.id ? response.data : item)));
      toast.success(response.data.include_in_carousel ? "Banner routed to marketing carousel" : "Banner removed from carousel");
      onRefresh();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Banner update failed");
    }
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl" data-testid="banner-review-panel">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-purple-300">
            <Megaphone className="h-4 w-4" /> Banner detection
          </div>
          <h3 className="font-headline text-xl font-semibold text-white">Marketing image candidates</h3>
        </div>
        <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-300">{items.length} detected</Badge>
      </div>
      {items.length === 0 ? (
        <div className="flex min-h-36 flex-col items-center justify-center rounded-xl border border-dashed border-slate-800 bg-slate-950/60 text-slate-500" data-testid="empty-banner-state">
          <ImageIcon className="mb-2 h-8 w-8" />
          No banner-like images detected in this batch.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((banner) => (
            <article key={banner.id} className="banner-glow overflow-hidden rounded-2xl border border-purple-500/30 bg-slate-950" data-testid={`banner-candidate-${banner.id}`}>
              <SecureImage fileId={banner.file_id} alt="Detected marketing banner" className="h-40 w-full object-cover" />
              <div className="flex items-center justify-between gap-3 p-4">
                <div>
                  <div className="text-sm font-semibold text-slate-100">Page {banner.page_number || "-"}</div>
                  <div className="text-xs text-slate-500">{banner.width} × {banner.height} · aspect {banner.aspect_ratio}</div>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input
                    data-testid={`banner-toggle-checkbox-${banner.id}`}
                    type="checkbox"
                    checked={Boolean(banner.include_in_carousel)}
                    onChange={() => toggle(banner)}
                    className="h-5 w-5 accent-purple-500"
                  />
                  Carousel
                </label>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
